import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Ty from '@mui/material/Typography';
import CircleIcon from '@mui/icons-material/Circle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTranslation } from 'react-i18next';
import { FeatureFlag, Res } from '@cbo/shared-library';
import { useTheme } from '@mui/material/styles';
import formatAgeIndicator from '../../../../utils/notificationsUtils/notificationsHelper';
import { useAdminRequests } from '../../../../admin/requests';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { useFeatureFlag } from '../../../../utils/hooks/useFeatureFlag';
import { downloadFile } from '../../../../labor/utilities/laborUtilities';

export interface NotificationCardProps {
  notification: Res.Notifications.Notification;
  renderDelete?: boolean;
  minHeightInPx?: string;
  handleRefetchNotifications: () => void;
  handleRightPanelClose?: () => void;
}

function NotificationCard(props: NotificationCardProps) {
  const { handleRightPanelClose } = props;
  const { dismissNotification, markNotificationAsRead, markNotificationAsUnread, getScheduledReportSignedUrl } =
    useAdminRequests();
  const { setSnackbarState } = useSnackbar();
  const isScheduledReportsEnabled = useFeatureFlag(FeatureFlag.scheduledReport);
  const theme = useTheme();

  const { t } = useTranslation(['translation'], {
    useSuspense: false,
  });
  const { notification, renderDelete, minHeightInPx, handleRefetchNotifications } = props;
  const currentDateInMinutes = Date.now() / 1000 / 60;

  async function updateNotificationReadFlag(notificationToUpdate: Res.Notifications.Notification) {
    try {
      if (notificationToUpdate.read) {
        await markNotificationAsUnread(notificationToUpdate.id);
      } else {
        await markNotificationAsRead(notificationToUpdate.id);
      }
      handleRefetchNotifications();
    } catch (e) {
      setSnackbarState({
        open: true,
        color: 'error',
        message: t('admin.allNotificationsDialog.errorUpdatingNotification'),
      });
    }
  }

  async function markNotificationAsDismissed(notificationToUpdate: Res.Notifications.Notification) {
    try {
      await dismissNotification(notificationToUpdate.id);
      handleRefetchNotifications();
    } catch (e) {
      setSnackbarState({
        open: true,
        color: 'error',
        message: t('admin.allNotificationsDialog.errorDismissNotification'),
      });
    }
  }

  async function handleScheduleReportsNotification(notificationToUpdate: Res.Notifications.Notification) {
    if (!isScheduledReportsEnabled) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fileNamePrefix = (notificationToUpdate as any)?.params?.fileNamePrefix ?? '';

    try {
      // Fetch the signed URL from the backend
      const { signedUrl } = await getScheduledReportSignedUrl({
        isScheduleReportExport: true,
        fileNamePrefix,
      });

      // Download the file using the signed URL
      downloadFile(signedUrl);

      // Mark notification as read and close panel
      await markNotificationAsRead(notification.id);
      await handleRefetchNotifications();
      await handleRightPanelClose?.();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading scheduled report:', error);
      setSnackbarState({
        open: true,
        color: 'error',
        message: t('admin.allNotificationsDialog.errorDownloadingReport'),
      });
    }
  }

  const chipColor = notification.read ? 'secondary' : 'primary';
  const isScheduleReportNotification = notification.eventType === 'scheduleReportsAdded';

  return (
    <Box
      data-testid='notification-card'
      sx={{
        display: 'flex',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        paddingTop: 1,
        paddingBottom: 0.5,
        width: '100%',
        minHeight: minHeightInPx,
        bgcolor: notification.priority && !notification.read ? 'background.default' : 'initial',
      }}
    >
      {/* unread icon */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          minWidth: '40px',
          marginTop: '4px',
        }}
      >
        {!notification.read && (
          <CircleIcon
            data-testid='unread-notification-icon'
            color='primary'
            sx={{
              fontSize: '12px',
            }}
          />
        )}
      </Box>

      {/* Text & body */}
      <Box
        sx={{
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
            marginRight: 2,
          }}
        >
          {/* Text & chip */}
          <Box>
            <Ty
              variant='body2'
              sx={{
                color: 'text.secondary',
                fontWeight: '700',
              }}
            >
              {!isScheduleReportNotification ? notification.siteName : ''}
            </Ty>
            <Ty variant='body2' data-testid='notification-message'>
              {notification.description}
            </Ty>
            {isScheduleReportNotification && (
              <Chip
                label={<Ty data-testid='openReport-icon'>{t('rightPanel.notifications.openReport')}</Ty>}
                data-testid='openReport-badge'
                color={chipColor}
                size='medium'
                sx={{
                  '& .MuiChip-label': {
                    color: notification.read ? theme.palette.secondary.main : theme.palette.primary.main,
                  },
                  marginTop: 1.5,
                }}
                clickable
                onClick={() => handleScheduleReportsNotification(notification)}
                icon={
                  <AttachFileIcon
                    sx={{
                      fontSize: '16px',
                    }}
                  />
                }
              />
            )}
            {notification.priority && (
              <Chip
                data-testid='priority-badge'
                label={t('rightPanel.notifications.priority')}
                color={chipColor}
                size='small'
                sx={{ marginTop: 1.5 }}
              />
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              marginTop: 0.5,
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: renderDelete ? 'flex-start' : 'space-between',
            }}
          >
            <Ty
              data-testid='age-indicator'
              variant='caption'
              color='text.secondary'
              sx={{
                minWidth: '82px',
                marginRight: 2,
              }}
            >
              {formatAgeIndicator(t, currentDateInMinutes, notification.createdAt)}
            </Ty>
            {renderDelete && (
              <Button
                data-testid='delete-notification'
                size='small'
                color='error'
                onClick={() => markNotificationAsDismissed(notification)}
              >
                {t('buttonText.delete')}
              </Button>
            )}
            <Button
              data-testid='mark-read-toggle'
              size='small'
              color='primary'
              onClick={() => updateNotificationReadFlag(notification)}
            >
              {notification.read ? t('rightPanel.notifications.markUnread') : t('rightPanel.notifications.markRead')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

NotificationCard.defaultProps = {
  renderDelete: false,
  minHeightInPx: '128px',
  handleRightPanelClose: undefined,
};

export default NotificationCard;
